import React, { useEffect, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { connect } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

// Components
import AssignContactEmptyPage from './partials/AssignContactEmptyState';
import ConquestIntegrationLayout from './ConquestIntegrationLayout';
import LoadingScreen from '@/views/components/common/LoadingScreen';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import ContactsPageNoData from '../contacts/ContactsPageNoData';
import LinkContactConfirmationModal from './partials/LinkContactConfirmationModal';
import { ContactsTable } from '@/views/components/tables';
import { ContactsTableFields } from '@/views/components/tables/ContactsTable';
import { EditContactModal } from '@/views/components/modals';
import { PrimaryButton } from '@/views/components/buttons';
import { SearchInput } from '@/views/components/inputs';
import { Typography } from '@capintel/component-lib/typography';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Redux actions
import { GET_CONTACTS, getMyContacts, resetEditContact, setPersistenceSource } from '@/redux/modules/contacts';
import { consumeLimit } from '@/redux/modules/rate-limits';
import { RateLimitType } from '@/redux/modules/types/rate-limits';
import { setIsActiveContactPostMessage } from '@/redux/modules/post-message';

// RTK Query
import { useClearPlanContactMutation, useGetPlanQuery, useLinkPlanToContactMutation } from '@/redux/api/planning';

// Types
import { ReduxState } from '@/redux/modules/types';
import { startLoaderAction } from '@/redux/modules/ui/loaders';
import GenericContact from '@/types/generic-contact';
import { SortingField } from '@/types/data';
import { useLoadingState } from '@/hooks/useLoadingState';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { ColumnSort } from '@tanstack/react-table';

// Utils
import { CONTACT_LAYOUT } from '@/utils/contacts';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import localStorageUtils from '@/utils/local-storage';
import { getTableSortedColumns } from '@/utils/data-table';
import { NAV_PATHS } from '@/utils/constants';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';
import { getFilteredDataSourceTabs } from '@/utils/data-source';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';
import notification from '@/utils/notification';

interface MatchParams {
  planId: string;
}

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => void;
  resetEditContact: (...args: Parameters<typeof resetEditContact>) => void;
  startLoaderAction: (...args: Parameters<typeof startLoaderAction>) => void;
  setIsActiveContactPostMessage: (...args: Parameters<typeof setIsActiveContactPostMessage>) => void;
  setPersistenceSource: (...args: Parameters<typeof setPersistenceSource>) => void;
  consumeLimit: (...args: Parameters<typeof consumeLimit>) => void;
};

type StateProps = {
  user?: User | null;
  contacts: GenericContact[];
  persistenceSource?: string;
  nextContactsToken?: string;
  editedContact: Partial<GenericContact>;
  isPostMessageActive: boolean;
};

type Props = RouteComponentProps<MatchParams> & StateProps & DispatchProps;

const i18nBasePath = 'Planning.Conquest.AssignContactPage';

const AssignContactPage = ({
  user,
  contacts,
  persistenceSource,
  nextContactsToken,
  editedContact,
  isPostMessageActive,
  getMyContacts,
  resetEditContact,
  startLoaderAction,
  setPersistenceSource,
  consumeLimit,
  match: url,
}: Props) => {
  const {
    activeDataSource,
    dataSourceTabs,
    hasContacts,
    isPlanLoading,
    isLinkingPlan,
    isContactLoading,
    contactsSearchQuery,
    linkConfirmationContact,
    showEditContactModal,
    debouncedSearchContacts,
    setLinkConfirmationContact,
    setShowEditContactModal,
    LoaderComponent,
    onServerSideSorting,
    onSelectDataSource,
    onClickContact,
    onLinkContact,
    onSaveContact,
    onContactsSortingChange,
    onContactsSearchChange,
    onLoadMoreContacts,
    onClose,
    onCreateContact,
  } = useAssignContactPage({
    user,
    contacts,
    nextContactsToken,
    url,
    isPostMessageActive,
    getMyContacts,
    resetEditContact,
    startLoaderAction,
    setIsActiveContactPostMessage,
    setPersistenceSource,
    consumeLimit,
  });
  return (
    <ConquestIntegrationLayout
      LoaderComponent={LoaderComponent}
      mainClassName="contacts-v2 planning-assign-contact-page"
      onClose={onClose}
      i18nBasePath={i18nBasePath}
      isLoading={isPlanLoading || isLinkingPlan}
    >
      <div className="content-section">
        <div className="section-header">
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
            {formatMessage(`${i18nBasePath}.ContactsTable.Title`)}
          </Typography>
          {(hasContacts || contactsSearchQuery) && (
            <>
              <SearchInput className="search-input-v2" onChange={onContactsSearchChange} />
              <div className="page-actions-section">
                <PrimaryButton className="primary-v2" onClick={() => onCreateContact()} data-testid="assignContact-button-createContact">
                  <FormattedMessage id={`${i18nBasePath}.ContactsTable.CreateContact`} />
                </PrimaryButton>
              </div>
            </>
          )}
        </div>
        {dataSourceTabs && (
          <div className="tabs-container">
            <DataSourceTabs disabled={isPlanLoading || isLinkingPlan} tabs={dataSourceTabs} value={activeDataSource} onChange={onSelectDataSource} />
          </div>
        )}

        {(hasContacts || isContactLoading) && (
          <ContactsTable
            multiselect={false}
            selectable={false}
            searchable={false}
            contacts={contacts}
            loading={isContactLoading}
            className="wide-first-column"
            onClick={onClickContact}
            user={user}
            layout={CONTACT_LAYOUT.CONTACT}
            onSortingChange={onContactsSortingChange}
            onLoadMore={onLoadMoreContacts}
            debouncedSearchContacts={debouncedSearchContacts}
            hasMore={nextContactsToken != null}
            persistenceSource={persistenceSource}
            showDataSourceTabs={false}
            serverSideSorting
            onServerSideSorting={onServerSideSorting}
            orderedFields={[ContactsTableFields.Fullname, ContactsTableFields.Uid, ContactsTableFields.planId]}
            isPostMessageActive={isPostMessageActive}
            setPersistenceSource={setPersistenceSource}
            setIsActiveContactPostMessage={setIsActiveContactPostMessage}
          />
        )}

        {!isContactLoading && !contactsSearchQuery && <AssignContactEmptyPage onCreateContact={onCreateContact} hasContacts={hasContacts} />}
        {!isContactLoading && !hasContacts && contactsSearchQuery && <ContactsPageNoData searchQuery={contactsSearchQuery} onAddContact={onCreateContact} />}
      </div>
      {linkConfirmationContact && (
        <LinkContactConfirmationModal
          showModal={!!linkConfirmationContact}
          onCancel={() => setLinkConfirmationContact(null)}
          onConfirm={() => {
            onLinkContact(linkConfirmationContact);
          }}
          contactName={`${linkConfirmationContact.fname} ${linkConfirmationContact.lname}`}
        />
      )}
      <EditContactModal
        user={user!}
        show={showEditContactModal}
        onCancel={() => setShowEditContactModal(false)}
        onSave={onSaveContact}
        contact={editedContact}
        isEditing
        saveLabelId={`${i18nBasePath}.CreateAndLinkContact`}
      />
    </ConquestIntegrationLayout>
  );
};

const useAssignContactPage = ({
  user,
  contacts,
  nextContactsToken,
  url,
  isPostMessageActive,
  getMyContacts,
  resetEditContact,
  startLoaderAction,
  setIsActiveContactPostMessage,
  setPersistenceSource,
  consumeLimit,
}: {
  user?: User | null;
  contacts: GenericContact[];
  url: { params: { planId: string } };
  nextContactsToken?: string;
  isPostMessageActive: boolean;
} & DispatchProps) => {
  const dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(user, DATA_SOURCE_TAB_CATEGORY.CONTACTS, isPostMessageActive);
  const history = useHistory();
  const hasContacts = contacts.length > 0;
  const { planId } = url.params;

  const {
    data: planData,
    isLoading: isPlanLoading,
    error: planError,
  } = useGetPlanQuery(planId, {
    skip: !planId,
  });

  // Use RTK Query mutation to link plan to contact
  const [linkPlanToContact, { isLoading: isLinkingPlan }] = useLinkPlanToContactMutation();
  const [clearPlanContact] = useClearPlanContactMutation();

  const contactLoadingStateDefiniton = {
    [GET_CONTACTS.type]: () => <LoadingScreen titleToLocalize={`${i18nBasePath}.Loading.GetContacts.Title`} />,
  };
  const { isLoading: isContactLoading } = useLoadingState(contactLoadingStateDefiniton);
  const LoaderComponent = isPlanLoading
    ? () => <LoadingScreen titleToLocalize={`${i18nBasePath}.Loading.GetPlan.Title`} />
    : () => <LoadingScreen titleToLocalize={`${i18nBasePath}.Loading.UpdatePlan.Title`} messageToLocalize={`${i18nBasePath}.Loading.UpdatePlan.Message`} />;

  const [contactsSearchQuery, setContactsSearchQuery] = useState('');
  const [contactsSortingFields, setContactsSortingFields] = useState<SortingField[]>([]);
  const [linkConfirmationContact, setLinkConfirmationContact] = useState<Nullable<GenericContact>>(null);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [activeDataSource, setActiveDataSource] = useState<string>(dataSourceTabs?.[0]?.value ?? '');

  const debouncedSearchContacts = useDebouncedCallback(() => {
    getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields, withPlanId: true });
  }, SEARCH_DEBOUNCE_TIME);

  const onSelectDataSource = (dataSourceValue: string) => {
    if (!user || !dataSourceTabs) return;

    setActiveDataSource(dataSourceValue);

    const activeDataSource = dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActiveContactPostMessage(true);
    } else {
      setIsActiveContactPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, persistenceSource);
    debouncedSearchContacts();
  };

  const onServerSideSorting = (sort: ColumnSort) => {
    const { serverSortingParams } = getTableSortedColumns(contactsSortingFields, sort);
    setContactsSortingFields(serverSortingParams);
    debouncedSearchContacts();
  };

  const onContactsSearchChange = (searchQuery: string) => {
    setContactsSearchQuery(searchQuery);
    startLoaderAction(GET_CONTACTS.type);
    debouncedSearchContacts();
  };

  const onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    setContactsSortingFields(contactsSortingFields);
  };

  const onLoadMoreContacts = () => {
    getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken, sort: contactsSortingFields, withPlanId: true });
  };

  const onCreateContact = async () => {
    resetEditContact();
    setShowEditContactModal(true);
  };

  const onClickContact = (contact: GenericContact) => {
    if (contact?.planId) {
      setLinkConfirmationContact(contact);
    } else {
      onLinkContact(contact);
    }
  };

  const onLinkContact = async (contact: GenericContact) => {
    const contactId = contact._id;
    setLinkConfirmationContact(null);
    try {
      if (contact.planId) {
        await clearPlanContact({ planId: contact.planId, contactId: contact?._id }).unwrap();
      }
      await linkPlanToContact({
        planId,
        contactId,
        previousPlanId: contact.planId,
      }).unwrap();
      goToImportAccounts();
    } catch (error) {
      notification.error(formatMessage(`${i18nBasePath}.Notification.Error.LinkContact.Message`));
    }
  };

  const onSaveContact = async (contact: GenericContact) => {
    const contactId = contact._id;
    consumeLimit(RateLimitType.CONTACTS);
    try {
      await linkPlanToContact({
        planId,
        contactId,
      }).unwrap();
      goToImportAccounts();
    } catch (error) {
      notification.error(formatMessage(`${i18nBasePath}.Notification.Error.LinkContact.Message`));
    }
  };

  const goToImportAccounts = () => {
    history.push(NAV_PATHS.PLANNING_IMPORT_ACCOUNTS.replace(':planId', planId));
  };

  const onClose = () => {
    history.push(NAV_PATHS.CONTACTS);
  };

  useEffect(() => {
    if (user) {
      // Reset the Persistence Source when the component mounts
      const isPostMessageDataSourceTabExist = dataSourceTabs?.find(tab => tab.isPostMessage);
      const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

      if (isInitialDataSourceTabPostMessage) {
        setIsActiveContactPostMessage(false);
      }
      resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, user);
    }
    getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields, withPlanId: true });
  }, [planId, user, debouncedSearchContacts]);

  // Handle plan data effects
  useEffect(() => {
    if (planError) {
      // If plan not found or error, redirect to home
      notification.error(formatMessage(`${i18nBasePath}.Notification.Error.FetchPlanAccounts.Message`));
      history.push('/');
      return;
    }

    if (planData) {
      // If plan has a contact assigned, redirect to import accounts
      if (planData.contactId) {
        goToImportAccounts();
      }
    }
  }, [planData, planError, history]);

  return {
    activeDataSource,
    dataSourceTabs,
    hasContacts,
    isPlanLoading,
    isLinkingPlan,
    isContactLoading,
    contactsSearchQuery,
    linkConfirmationContact,
    showEditContactModal,
    debouncedSearchContacts,
    setLinkConfirmationContact,
    setShowEditContactModal,
    LoaderComponent,
    onServerSideSorting,
    onSelectDataSource,
    onClickContact,
    onLinkContact,
    onSaveContact,
    onContactsSortingChange,
    onContactsSearchChange,
    onLoadMoreContacts,
    onClose,
    onCreateContact,
  };
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user,
  contacts: state.contacts.contacts,
  persistenceSource: state.contacts.persistenceSource,
  nextContactsToken: state.contacts.nextContactsToken,
  editedContact: state.contacts.editContact,
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default connect(mapStateToProps, {
  getMyContacts,
  resetEditContact,
  startLoaderAction,
  setIsActiveContactPostMessage,
  setPersistenceSource,
  consumeLimit,
})(AssignContactPage);
